import {usePlantRunningTimes} from '@domain/running-times'
import {usePlantConfig} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {Iso8601, RunningTime} from '@hconnect/common/types'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {CalendarTodayOutlined} from '@mui/icons-material'
import {Box, Chip, SxProps, Theme, Typography} from '@mui/material'
import {isEmpty} from 'lodash'
import moment from 'moment-timezone'
import {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'

import {OperationHoursLegendTooltip, OperationHoursList} from '../components'
import {RUNNING_TIMES_WIDGET_NAMESPACE} from '../consts'
import {getEquipmentStoppageCodesByStoppageType} from '../mappers'

type Props = {
  plantId: string
  from?: Iso8601
  to?: Iso8601
  timezone: string
  clipToCurrentTime?: boolean
  timeRangeLabel?: string
  onEquipmentClick?: (equipmentId: string) => void
  cardBoxSx?: SxProps<Theme>
  isMobile?: boolean
  hideLegend?: boolean
  renderRunningTimeTooltipFooter?: (rt: RunningTime) => ReactNode
}

const cardBoxPx = {xs: 2, md: 3}
const MIN_ITEMS_FOR_TWO_COLUMNS_VIEW = 5

export const OperationHoursContainer = ({
  plantId,
  from,
  to,
  timezone,
  clipToCurrentTime = false,
  timeRangeLabel,
  onEquipmentClick,
  cardBoxSx,
  isMobile = false,
  hideLegend = false,
  renderRunningTimeTooltipFooter
}: Props) => {
  const {t, i18n} = useTranslation(RUNNING_TIMES_WIDGET_NAMESPACE)
  const isTimeRangeSupported = !!from && !!to
  // set seconds and milliseconds to 0 to optimize the number of OperationHours rerenders
  const currentTime = moment.utc().tz(timezone).set({second: 0, millisecond: 0}).toISOString()
  const {
    data: plantConfig,
    isLoading: isPlantConfigLoading,
    isError: isPlantConfigError,
    isRefetching: isPlantConfigRefetching,
    refetch: refetchPlantConfig
  } = usePlantConfig({plantId, language: i18n.language})
  const {
    data: runningTimePerformances,
    isLoading: isRunningTimesLoading,
    isError: isRunningTimesError,
    isRefetching: isRunningTimesRefetching,
    refetch: refetchRunningTimes
  } = usePlantRunningTimes(
    {
      plantId,
      from,
      to
    },
    {enabled: isTimeRangeSupported}
  )

  const isOneColumnView =
    isMobile || (runningTimePerformances?.length ?? 0) < MIN_ITEMS_FOR_TWO_COLUMNS_VIEW

  return (
    <CardBox
      sx={{
        ...cardBoxSx,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        px: 0,
        pl: cardBoxPx
      }}
      data-test-id="operation-hours-card"
    >
      <Box
        sx={{
          display: 'flex',
          pr: cardBoxPx
        }}
      >
        <Box flex={1}>
          <Typography variant="h3" mb={2} display="flex" alignItems="center">
            {t('operationHours.cardTitle')}
            {hideLegend && <OperationHoursLegendTooltip />}
          </Typography>
        </Box>
        {timeRangeLabel && (
          <Chip icon={<CalendarTodayOutlined />} label={timeRangeLabel} size="small" sx={{ml: 1}} />
        )}
      </Box>
      <DataContentWrapper
        showLoader={isRunningTimesLoading || isPlantConfigLoading}
        isError={isRunningTimesError || isPlantConfigError}
        isRefetching={isRunningTimesRefetching || isPlantConfigRefetching}
        isNoData={isEmpty(runningTimePerformances) || isEmpty(plantConfig)}
        retryFunction={() => {
          if (isPlantConfigError) void refetchPlantConfig()
          if (isRunningTimesError) void refetchRunningTimes()
        }}
        progressSx={{color: 'primary.main'}}
        noDataSettings={{
          ...(!isTimeRangeSupported && {
            noDataMessage: t('operationHours.message.unsupportedTimeRange')
          })
        }}
      >
        <Box sx={{flex: 1, overflowY: 'auto', overflowX: 'hidden', pr: cardBoxPx}}>
          {from && to && (
            <OperationHoursList
              runningTimePerformances={runningTimePerformances}
              from={from}
              to={clipToCurrentTime ? currentTime : to}
              timezone={timezone}
              stoppageCodesByType={getEquipmentStoppageCodesByStoppageType(plantConfig)}
              onEquipmentClick={onEquipmentClick}
              oneColumnView={isOneColumnView}
              hideLegend={hideLegend}
              renderRunningTimeTooltipFooter={renderRunningTimeTooltipFooter}
            />
          )}
        </Box>
      </DataContentWrapper>
    </CardBox>
  )
}
